import { curry, path, pluck, reverse } from 'ramda'
import { isIosApp, isAndroidApp, isApp, appIsGteVersion } from '../../common/ambient'

/**
 * Checks if the app version meets requirements for subscription and redirects to store if needed
 * @param {string} subscription - The subscription product name (e.g., 'awn-plus-for-teams-yearly')
 * @returns {boolean} - true if redirect occurred (version too old), false if version is OK
 */
export const doAppVersionRedirect = (subscription) => {
  // Check version requirements
  if (!appIsGteVersion('4.5.0') ||
      (!appIsGteVersion('4.6.0') && /for-teams/.test(subscription))) {

    // Determine store URL based on platform
    const storeUrl = isIosApp()
      ? 'https://apps.apple.com/us/app/ambient-weather-network/id1426025887'
      : 'https://play.google.com/store/apps/details?id=net.ambientweather.dashboard'

    // Alert user to update
    window.alert('Please update your app to the latest version to subscribe.')

    // Open store
    window.open(storeUrl, '_blank')

    return true
  }

  return false
}

// Warning; array MUST be ordered from lowest tier to highest tier
export const CAM_TIERS = [{
  label: 'Standard',
  timelapseRes: '640x320',
  price: 'Free',
  regenRate: '4 times per day',
  banner: 'Standard',
  sampling: '5 minutes',
  storage: '1 day',
  mapIcon: 'Standard',
  productName: '',
  term: 'both'
}, {
  label: 'Silver Monthly',
  price: '$4.99/month',
  buyPrice: 4.99,
  timelapseRes: '1280x720',
  regenRate: '6 times per day',
  banner: 'HD',
  sampling: '3 minutes',
  storage: '30 days',
  mapIcon: 'Silver',
  productName: 'awn-camera-tier1-monthly',
  term: 'monthly'
}, {
  label: 'Silver Annual',
  price: '$4.16/month', // really $49.92
  buyPrice: 49.99,
  timelapseRes: '1280x720',
  regenRate: '6 times per day',
  banner: 'HD',
  sampling: '3 minutes',
  storage: '30 days',
  mapIcon: 'Silver',
  productName: 'awn-camera-tier1-yearly',
  term: 'yearly'
}, {
  label: 'Gold Monthly',
  price: '$9.99/month',
  buyPrice: 9.99,
  timelapseRes: '1920x1080',
  regenRate: '8 times per day',
  banner: 'Full HD',
  sampling: '3 minutes',
  storage: '60 days',
  mapIcon: 'Gold',
  productName: 'awn-camera-tier2-monthly',
  term: 'monthly'
}, {
  label: 'Gold Annual',
  price: '$8.33/month', // really $99.96
  buyPrice: 99.99,
  timelapseRes: '1920x1080',
  regenRate: '8 times per day',
  banner: 'Full HD',
  sampling: '3 minutes',
  storage: '60 days',
  mapIcon: 'Gold',
  productName: 'awn-camera-tier2-yearly',
  term: 'yearly'
}]

const camProductNames = pluck('productName', CAM_TIERS)

export const USE_NEW_CELL_PLAN = Date.now() > new Date('2024-10-01 00:00:00').getTime()

export const CELL_SUBSCRIPTIONS = [{
  productName: 'kestrelmet-one-year-subscription-new',
  label: 'KestrelMet Standard 1 Year Cellular Plan',
  price: 99.99,
  SKU: 281055
}, {
  productName: 'kestrelmet-one-year-subscription',
  label: 'KestrelMet Standard 1 Year Cellular Plan',
  price: 49.99, // really 49.92
  SKU: 281055
}, {
  productName: 'kestrelmet-three-year-subscription',
  label: 'KestrelMet Standard 3 Year Cellular Plan',
  price: 150,
  SKU: 281057
}, {
  productName: 'kestrelmet-pro-one-year-subscription',
  label: 'KestrelMet Pro 1 Year Cellular Plan',
  price: 120,
  SKU: 281053
}, {
  productName: 'kestrelmet-pro-three-year-subscription',
  label: 'KestrelMet Pro 3 Year Cellular Plan',
  price: 360,
  SKU: 281058
}]

export const USER_SUBSCRIPTIONS = [{
  productName: 'awn-plus-account-monthly',
  price: '$4.99/month',
  appSubscriptionId: 281024,
  displayName: 'AWN+ Individual',
  forecastDays: 10
}, {
  productName: 'awn-plus-account-yearly',
  price: '$49.99/year',
  appSubscriptionId: 281025,
  displayName: 'AWN+ Individual',
  forecastDays: 10
}, {
  productName: 'awn-plus-for-teams-monthly',
  price: '$9.99/month',
  appSubscriptionId: 281026,
  displayName: 'AWN+ Teams',
  forecastDays: 15
}, {
  productName: 'awn-plus-for-teams-yearly',
  price: '$99.99/year',
  appSubscriptionId: 281027,
  displayName: 'AWN+ Teams',
  forecastDays: 15
}, {
  productName: 'awn-plus-for-teams-free',
  price: 'Free',
  displayName: 'AWN+ Teams (Free)',
  forecastDays: 15
}, {
  productName: 'awn_sms_notifications',
  price: '$1.50/100 SMS',
  displayName: 'SMS Notifications'
}]

export const displayPriceForSubscription = (sub) => {
  if (!sub) {
    return 'Unknown Subscription'
  }
  if (subIsPlus(sub) && sub.promoCode && !sub.autoRenewChangeIsAllowed) {
    return 'Free'
  }
  if (sub.price) {
    return sub.currency + sub.price
  }
  if (sub.pricingInfo) {
    return sub.pricingInfo.currency + sub.pricingInfo.amount
  }
  let productName = sub.productName
  const subscription = [...USER_SUBSCRIPTIONS, ...CELL_SUBSCRIPTIONS, ...CAM_TIERS].find(entry => entry.productName === productName)
  if (typeof produceName === 'object') {
    productName = productName.productName
  }
  if (!subscription) {
    return 'Unknown Subscription'
  }
  return typeof subscription.price === 'string' ? subscription.price : '$' + subscription.price.toFixed(2)
}
export const isCellularDevice = (device) => path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000'
export const isCellularSubscription = (productName) => CELL_SUBSCRIPTIONS.find(entry => entry.productName === productName)
export const hasCellularSubscription = curry((user, macAddress) => {
  return (
    (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress === macAddress)
      .filter(sub => isCellularSubscription(sub.productName))
      .length > 0
  )
})
export const hasCellularDevice = (devices) => {
  return (
    (devices || [])
      .filter(device => isCellularDevice(device))
      .length > 0
  )
}
export const isKestrelMet = (device) =>
  path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000' ||
  path(['lastData', 'stationtype'], device) === 'NK_KestrelMet6000W'

export const hasRainWiseStatus = isKestrelMet

export const userHasPayments = path(['info', 'payments'])

export const userHasCardOnFile = path(['info', 'payments', 'defaultPaymentMethod'])
export const deviceHasSubscription = curry((user, macAddress) => {
  return (path(['info', 'payments', 'subscriptions'], user) || []).find(sub => sub.macAddress === macAddress)
})

export const deviceHasThisSub = curry((user, macAddress, productName) => {
  return (
    (path(['info', 'payments', 'subscriptions'], user) || [])
      .filter(sub => sub.macAddress == macAddress)
      .find(sub => sub.productName === productName)
  )
})

export const deviceCamTierI = curry((user, macAddress) => {
  const lastTierIndex = CAM_TIERS.length - 1
  const i = reverse(camProductNames).findIndex((prodName) => deviceHasThisSub(user, macAddress, prodName))
  return i > -1 ? lastTierIndex - i : 0
})

export const deviceCamTierObj = curry((user, macAddress) => {
  const i = deviceCamTierI(user, macAddress)
  const ret = Object.assign({ tier: i }, CAM_TIERS[i])
  return ret
})

// Stripe Subscription Object
// status enum
// Possible values are incomplete, incomplete_expired, trialing, active, past_due, canceled, or unpaid.
// also "free" which is created internally
export const deviceSubscriptionStatus = curry((user, macAddress, productName) => {
  const sub = (path(['info', 'payments', 'subscriptions'], user) || [])
    .filter(sub => sub.macAddress === macAddress)
    .find(sub => sub.productName === productName)
  return (sub ? sub.status : '')
})

const activeSubscriptionStatuses = ['active', 'trialing', 'past_due', 'free']
export const subIsActive = sub => {
  if (!sub) {
    return false
  }
  if (typeof sub.provisionAccess !== 'undefined') {
    return sub.provisionAccess
  }
  return activeSubscriptionStatuses.includes(sub.status)
}

// this can take the user state OR the user object (contained in user.info)
export const isPlus = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const plusSub = getPlusSub(user)
  if (plusSub && subIsActive(plusSub)) {
    return plusSub
  }
  if (theUser.roles && theUser.roles.includes('awn-plus')) {
    return {
      productName: 'awn-plus-free'
    }
  } else if (theUser.roles && theUser.roles.includes('awn-plus-teams')) {
    return {
      productName: 'awn-plus-for-teams-free'
    }
  }
  return false
}

export const displayCancelPlusOnIosMessage = user => {
  const allPlusSubs = getAllPlusSubs(user)
  if (allPlusSubs.length > 1) {
    const iosSubs = allPlusSubs.filter(subIsIos)
    if (iosSubs[0]) {
      if (!iosSubs[0].cancelAtPeriodEnd) {
        return true
      }
    }
  }
}

export const getAllPlusSubs = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const subscriptions = path(['payments', 'subscriptions'], theUser) || []
  return subscriptions.filter(subIsPlus)
}

export const getPlusSub = user => {
  const theUser = path(['info'], user) || user
  if (!theUser) return false
  const subs = getAllPlusSubs(user)
  if (!subs.length) {
    return false
  } else if (subs.length === 1) {
    return subs[0]
  }
  return subs.find(s => s.promoCode) // favor the one with a promo code
}
export const plusIsTrial = user => {
  return getPlusSub(user) && getPlusSub(user).status === 'trialing'
}
export const plusSubHasCode = user => {
  return getPlusSub(user) && getPlusSub(user).promoCode
}

export const subIsPlus = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return /^awn-plus-/.test(sub.productName)
}
export const subIsTeams = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return /^awn-plus-for-teams-/.test(sub.productName)
}
export const subIsAndroid = sub => {
  if (!sub) {
    return false
  }
  return sub.googleProductId
}
export const subIsIos = sub => {
  if (!sub) {
    return false
  }
  return sub.appleProductId
}
export const subIsIap = sub => {
  if (!sub) {
    return false
  }
  return subIsAndroid(sub) || subIsIos(sub)
}

/**
 * Detects if an IAP subscription's original platform doesn't match current device
 */
export const hasIapPlatformMismatch = (sub) => {
  if (!subIsIap(sub)) {
    return false
  }

  // iOS subscription but not on iOS device
  if (subIsIos(sub) && !isIosApp()) {
    return true
  }

  // Android subscription but not on Android device
  if (subIsAndroid(sub) && !isAndroidApp()) {
    return true
  }

  return false
}

/**
 * Gets the appropriate store management URL for an IAP subscription
 */
export const getIapManagementUrl = (sub) => {
  if (!subIsIap(sub)) {
    return null
  }

  if (subIsIos(sub)) {
    return 'https://apps.apple.com/account/subscriptions'
  } else if (subIsAndroid(sub)) {
    return `https://play.google.com/store/account/subscriptions?sku=${sub.googleProductId}&package=net.ambientweather.dashboard`
  }

  return null
}

/**
 * Gets the store name for display
 */
export const getIapStoreName = (sub) => {
  return subIsIos(sub) ? 'Apple App Store' : 'Google Play Store'
}

export const subIsIosDowngradedAnnual = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  return subIsIos(sub) && /yearly/.test(sub.productName) && sub.willDowngradeAtPeriodEnd
}
export const subIsMonthly = sub => {
  if (!sub || !sub.productName) {
    return false
  }
  if (/monthly/.test(sub.productName)) {
    return true
  }
  if (subIsIosDowngradedAnnual(sub)) {
    return true
  }
  return false
}

export const subStatusDisplay = sub => {
  if (!sub || !sub.status) {
    return ''
  }
  if (sub.status === 'trialing') {
    return 'Free Trial'
  }
  return sub.status.charAt(0).toUpperCase() + sub.status.slice(1).replace('_', ' ')
}

export const getExpiredCellularSubscription = curry((user, macAddress) => {
  const transactions = path(['info', 'payments', 'transactions'], user) || []
  const cellularTransactions = transactions
    .filter(transaction => 
      transaction.macAddress === macAddress && 
      isCellularSubscription(transaction.productName)
    )
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
  
  if (cellularTransactions.length > 0 && cellularTransactions[0].type === 'customer.subscription.deleted') {
    return cellularTransactions[0]
  }
  return null
})
// Helper to get subscription config by product name
export const getSubscriptionConfig = (productName) => {
  return USER_SUBSCRIPTIONS.find(sub => sub.productName === productName)
}

// Helper to get app subscription ID from product name
export const getAppSubscriptionId = (productName) => {
  const config = getSubscriptionConfig(productName)
  return config ? config.appSubscriptionId : null
}

// Trigger native IAP flow for iOS/Android apps
export const triggerNativeIAP = (productName, userId) => {
  const subscriptionConfig = getSubscriptionConfig(productName)
  const appSubscriptionId = subscriptionConfig && subscriptionConfig.appSubscriptionId
  
  if (!appSubscriptionId || !userId) {
    console.error('triggerNativeIAP: Missing appSubscriptionId or userId', { productName, userId, appSubscriptionId })
    return false
  }
  
  try {
    if (typeof window !== 'undefined') {
      // iOS IAP flow
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.buySubscriptionWebNotification) {
        window.webkit.messageHandlers.buySubscriptionWebNotification.postMessage({ 
          userId, 
          subscriptionId: appSubscriptionId 
        })
        return true
      }
      
      // Android IAP flow
      if (window.ANDROID && typeof window.ANDROID.buySubscriptionWebNotification === 'function') {
        window.ANDROID.buySubscriptionWebNotification(appSubscriptionId, userId)
        return true
      }
    }
    
    console.error('triggerNativeIAP: Native IAP interface not available')
    return false
  } catch (error) {
    console.error('triggerNativeIAP: Error triggering IAP', error)
    return false
  }
}

// Helper to get subscription display name
export const getSubscriptionDisplayName = (subscription) => {
  if (!subscription || !subscription.productName) {
    return 'AWN+'
  }

  const config = getSubscriptionConfig(subscription.productName)
  let displayName = config ? config.displayName : subscription.nickname

  // Add downgrade notice if applicable
  if (subscription.willDowngradeAtPeriodEnd) {
    let downgradeToName = 'monthly'
    if (subscription.willDowngradeTo) {
      const downgradeConfig = getSubscriptionConfig(subscription.willDowngradeTo)
      if (downgradeConfig) {
        downgradeToName = downgradeConfig.displayName
      }
    }
    displayName += ` (will downgrade to ${downgradeToName} at end of period)`
  }
  if (subscription.promoCode) {
    displayName += ` (Gift Code: ${subscription.promoCode})`
  }

  return displayName
}

// only returns something is the app has configured the product details
export const getAwnPlusAppPrice = (device, annual) => {
  const productName = annual ? 'awn-plus-account-yearly' : 'awn-plus-account-monthly'
  const appSubscriptionId = getAppSubscriptionId(productName)
  if (isApp() && appSubscriptionId) {
    if (device.appProductDetails && Array.isArray(device.appProductDetails)) {
      const p = device.appProductDetails.find(item => parseInt(item.id, 10) === appSubscriptionId)
      if (p && p.displayPrice) {
        return p.displayPrice.replace(/USD\s+/, '$')
      }
    }
    return '- -'
  }
}

// only returns something is the app has configured the product details
export const getAwnTeamsAppPrice = (device, annual) => {
  const productName = annual ? 'awn-plus-for-teams-yearly' : 'awn-plus-for-teams-monthly'
  const appSubscriptionId = getAppSubscriptionId(productName)
  if (isApp() && appSubscriptionId) {
    if (device.appProductDetails && Array.isArray(device.appProductDetails)) {
      const p = device.appProductDetails.find(item => parseInt(item.id, 10) === appSubscriptionId)
      if (p && p.displayPrice) {
        return p.displayPrice.replace(/USD\s+/, '$')
      }
    }
    return '- -'
  }
}

export const freeTrialUsed = (user, subKey) => {
  return path(['info', 'payments', 'freeTrialsUsed', subKey], user)
}

export const showAwnFreeTrial = user => {
  const freeTrialsUsed = path(['info', 'payments', 'freeTrialsUsed'], user) || {}
  // Check if any key starts with 'awn-plus'
  const hasUsedAwnPlusTrial = Object.keys(freeTrialsUsed).some(key => key.startsWith('awn-plus'))
  return !hasUsedAwnPlusTrial
}

export const awnPlusAnnualMonthlySwitchAvail = (user, sub) => {
  // If sub is provided, use it for specific subscription checks
  if (sub) {
    // Check if it's an AWN Plus subscription
    if (!/awn-plus/.test(sub.productName)) return false

    // Check user-level conditions (user.plus exists and is not IAP)
    if (!awnPlusAnnualMonthlySwitchAvail(user)) return false

    // Check if it's an iOS downgraded annual
    if (subIsIosDowngradedAnnual(sub)) return false

    // Check if it's a promo code with plan change not allowed
    if (sub.promoCode && !sub.planChangeIsAllowed) return false

    return true
  }

  // Legacy behavior when called with just user (for backward compatibility)
  const userSub = user.plus
  if (!userSub) return false
    if (subIsIap(userSub)) {
    if (subIsIos(userSub) && !isIosApp()) {
      return null  // Apple subscription but not on iOS device
    }
    if (!subIsIos(userSub) && !isAndroidApp()) {
      return null  // Google subscription but not on Android device
    }
  }
  return true
}

export const getHourlyForecastLimit = user => {
  return user.plus ? 240 : 72 // Plus members get 10 days (240 hours), regular users get 3 days (72 hours)
}

export const getDailyForecastLimit = user => {
  if (!user.plus) return 10 // Regular users get 10 days
  
  const plusSub = getPlusSub(user)
  if (plusSub && plusSub.productName) {
    const config = getSubscriptionConfig(plusSub.productName)
    if (config && config.forecastDays) {
      return config.forecastDays
    }
  }
  
  return 10 // Default for Plus members
}

// Generic curried function for subscription pricing
export const createSubscriptionPriceGetter = curry((device, tier, isAnnual, useAppPrice) => {
  const isTeams = tier === 'teams'
  const productName = `awn-plus-${isTeams ? 'for-teams' : 'account'}-${isAnnual ? 'yearly' : 'monthly'}`
  const config = getSubscriptionConfig(productName)
  
  // Only use app price when explicitly requested AND in app context
  if (useAppPrice && isApp()) {
    const appPrice = isTeams ? getAwnTeamsAppPrice(device, isAnnual) : getAwnPlusAppPrice(device, isAnnual)
    if (appPrice && appPrice !== '- -') {
      const priceNum = parseFloat(appPrice.replace(/[^0-9.]/g, ''))
      return {
        price: appPrice,
        monthlyPrice: isAnnual ? `$${(priceNum / 12).toFixed(2)}` : appPrice.replace('/month', ''),
        billedText: isAnnual ? `Billed annually ${appPrice}/year` : null,
        rawPrice: priceNum
      }
    }
  }
  
  // Default to hardcoded Stripe prices from config
  const stripePrice = config ? config.price : (isTeams ? 
    (isAnnual ? '$99.99/year' : '$9.99/month') : 
    (isAnnual ? '$49.99/year' : '$4.99/month'))
  
  const priceNum = parseFloat(stripePrice.replace(/[^0-9.]/g, ''))
  
  return {
    price: stripePrice,
    monthlyPrice: isAnnual ? `$${(priceNum / 12).toFixed(2)}` : stripePrice.replace('/month', ''),
    billedText: isAnnual ? `Billed annually ${stripePrice}` : null,
    rawPrice: priceNum
  }
})

export { default as AddCard } from './AddCard'
export { default as AllPayments } from './AllPayments'
export { default as DisplayCard } from './DisplayCard'
export { default as DeleteCard } from './DeleteCard'
export { default as ManageSubscriptions } from './ManageSubscriptions'
export { default as DisplaySubscription } from './DisplaySubscription'
export { default as AddSubscription } from './AddSubscription'
export { default as EnhancedCamModal } from './EnhancedCamModal'
export { default as CellPlanModal } from './CellPlanModal'
export { default as PlusSubscriptionModal } from './PlusSubscriptionModal'
export { default as SubscribeProcess } from './SubscribeProcess'
export { billingDeferSubscription, billingUpdateUserEmail } from './billingAccess'
export { default as AdminUserSubscriptions } from './AdminUserSubscriptions';
export { default as GiftCodeForm } from './GiftCodeForm';
export { default as SmsAutoRefillToggle } from './SmsAutoRefillToggle';
export { default as SmsMeteredInfo } from './SmsMeteredInfo';
export { default as PlusPlansFeatures } from './PlusPlansFeatures';
export { default as FeatureHub } from './FeatureHub';
export { default as SectionActions } from './SectionActions';
